import { useQuery } from "@apollo/client";
import { useRef, useState } from "react";
import { FaCoins, FaCrown } from "react-icons/fa";
import { toast } from "react-toastify";
import { GET_PURCHASE_TOKENS_MENU } from "../../../../queries/mutation/purchase/purchase";
import LoadingPage from "../../../LoadingPage";

// const stripePromise = loadStripe(process.env.STRIPE_CLIENT_KEY ?? ""); // Your Test Publishable Key

export type Option = {
  id: number;
  amount: number;
  cost: number;
};

const ShopDiscordTokens = () => {
  const bottomRef = useRef<HTMLDivElement>(null);

  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    "cashapp" | "onlyfans" | "fansly" | null
  >(null);
  const [isPaying, setIsPaying] = useState(false);

  const { loading, error, data } = useQuery(GET_PURCHASE_TOKENS_MENU);

  const onSelect = (option: Option) => {
    setSelectedOption(option);
    setSelectedPaymentMethod(null); // Reset payment method when selecting new package
    // Open modal
    (
      document.getElementById("payment_modal") as HTMLDialogElement
    )?.showModal();
  };

  const closeModal = () => {
    setSelectedPaymentMethod(null);
    (document.getElementById("payment_modal") as HTMLDialogElement)?.close();
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  if (loading) {
    return <LoadingPage />;
  }
  const options: Option[] = data.getPurchaseTokensMenu ?? [];

  const handleScrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // we need to convert cost (in cents) to dollars
  const formattedOption = options.map((o) => {
    return { ...o, cost: o.cost / 100 };
  });

  // Calculate Onlyfans/Fansly price (20% increase)
  const getInflatedPrice = (cost: number) => {
    return (cost * 1.2).toFixed(2);
  };
  return (
    <div className="min-h-screen bg-base-200 flex flex-col items-center p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-6xl">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <FaCoins className="text-yellow-500 text-4xl animate-pulse" />
            <h1 className="text-4xl font-bold text-primary">Shop Crim Coins</h1>
            <FaCoins className="text-yellow-500 text-4xl animate-pulse" />
          </div>
          <p className="text-base-content/70 text-lg">
            Choose a package to purchase Crim Coins
          </p>
          <p className="text-base-content/60 text-sm mt-2">
            💡 Larger packages give you more value!
          </p>
        </div>

        {/* Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {formattedOption.map((o, index) => {
            // Base rate: $10 = 100 coins, so $1 = 10 coins
            const baseRate = 10; // coins per dollar
            const expectedCoins = o.cost * baseRate; // How many coins at base rate
            const bonusCoins = o.amount - expectedCoins;
            const percentMore = Math.floor((bonusCoins / expectedCoins) * 100);
            const isBestDeal = index === 6;

            return (
              <div
                key={o.id}
                className={`relative flex flex-col justify-between rounded-xl border-2 shadow-lg p-6 bg-base-100 cursor-pointer transition-all duration-300 transform hover:scale-105 ${"border-base-300 hover:border-primary/50 hover:shadow-xl"} ${
                  isBestDeal ? "ring-2 ring-yellow-500" : ""
                }`}
                onClick={() => onSelect(o)}
              >
                {/* Best Deal Badge */}
                {isBestDeal && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                      <FaCrown className="text-sm" />
                      BEST VALUE
                      <FaCrown className="text-sm" />
                    </div>
                  </div>
                )}

                {/* Content */}
                <div className="text-center space-y-4 mt-2">
                  {/* Coin Amount */}
                  <div>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <FaCoins className="text-yellow-500 text-2xl" />
                      <span className="text-3xl font-bold text-primary">
                        {o.amount.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-base-content/60 text-sm">
                      Crim Coins
                    </div>
                  </div>

                  {/* Bonus Info */}
                  {index !== 0 && (
                    <div className="pt-2">
                      <div className="bg-success/10 border border-success/20 rounded-lg px-3 py-2">
                        <div className="text-success font-semibold text-sm flex items-center justify-center gap-2">
                          <span>🎁</span>
                          <span>+{percentMore}% Bonus</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Divider */}
                  <div className="divider my-2"></div>

                  {/* Pricing Button */}
                  <div>
                    <button className="btn btn-primary btn-wide">
                      Click Here for Pricing
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment Modal */}
      <dialog id="payment_modal" className="modal modal-bottom sm:modal-middle">
        <div className="modal-box bg-base-100 border border-base-300 max-w-3xl">
          <h3 className="font-bold text-2xl text-center text-base-content mb-2 flex items-center justify-center gap-2">
            <FaCoins className="text-yellow-500" />
            {selectedOption?.amount.toLocaleString()} Crim Coins
          </h3>
          <div className="divider divider-primary m-2"></div>

          {!selectedPaymentMethod ? (
            <div className="space-y-4">
              <p className="text-center text-base-content/70 mb-4">
                Select your payment method:
              </p>

              {/* Cashapp/Crypto Option */}
              <div
                className="bg-base-200 rounded-lg p-4 border-2 border-success hover:bg-base-300 cursor-pointer transition-all"
                onClick={() => setSelectedPaymentMethod("cashapp")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">💵</span>
                    <div>
                      <h4 className="font-bold text-base-content text-lg">
                        Cash App
                      </h4>
                      <p className="text-xs text-base-content/60">
                        Standard price
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-success">
                      ${selectedOption?.cost.toFixed(2)}
                    </div>
                    <div className="text-xs text-base-content/50">USD</div>
                  </div>
                </div>
              </div>

              {/* OnlyFans Option */}
              <div
                className="bg-base-200 rounded-lg p-4 border-2 border-primary hover:bg-base-300 cursor-pointer transition-all"
                onClick={() => setSelectedPaymentMethod("onlyfans")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src="https://raw.githubusercontent.com/edent/SuperTinyIcons/master/images/svg/onlyfans.svg"
                      alt="OnlyFans"
                      className="w-8 h-8 rounded-lg"
                    />
                    <div>
                      <h4 className="font-bold text-base-content text-lg">
                        OnlyFans
                      </h4>
                      <p className="text-xs text-warning">
                        Includes Processing Fees
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-primary">
                      ${getInflatedPrice(selectedOption?.cost || 0)}
                    </div>
                    <div className="text-xs text-base-content/50">USD</div>
                  </div>
                </div>
              </div>

              {/* Fansly Option */}
              <div
                className="bg-base-200 rounded-lg p-4 border-2 border-primary hover:bg-base-300 cursor-pointer transition-all"
                onClick={() => setSelectedPaymentMethod("fansly")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src="https://cdn.beacons.ai/user_content/3EC1pHDtDwh0uQVFLS97BJVPVaH2/link_images/f0fc8769-832f-42ed-a13a-6d07a3165cb9.png"
                      alt="Fansly"
                      className="w-8 h-8 rounded-lg"
                    />
                    <div>
                      <h4 className="font-bold text-base-content text-lg">
                        Fansly
                      </h4>
                      <p className="text-xs text-warning">
                        Includes Processing Fees
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-primary">
                      ${getInflatedPrice(selectedOption?.cost || 0)}
                    </div>
                    <div className="text-xs text-base-content/50">USD</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Back Button */}
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setSelectedPaymentMethod(null)}
              >
                ← Back to payment methods
              </button>

              {/* Payment Details for Cash App */}
              {selectedPaymentMethod === "cashapp" && (
                <div className="bg-base-200 rounded-lg p-6 border-2 border-success">
                  <div className="text-center mb-4">
                    <span className="text-4xl">💵</span>
                    <h4 className="font-bold text-2xl text-base-content mt-2">
                      Cash App Payment
                    </h4>
                    <p className="text-success text-3xl font-bold mt-2">
                      ${selectedOption?.cost.toFixed(2)} USD
                    </p>
                  </div>
                  <div className="divider"></div>
                  <div className="space-y-3">
                    <p className="text-base-content/70 text-sm">
                      Send payment to:
                    </p>
                    <div className="bg-base-100 rounded-lg p-4 flex items-center justify-between">
                      <code className="text-success font-mono text-lg">
                        $xcrimsonkitten
                      </code>
                      <button
                        onClick={() =>
                          copyToClipboard("$xcrimsonkitten", "Cash App")
                        }
                        className="btn btn-sm btn-primary"
                      >
                        Copy
                      </button>
                    </div>
                    <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 mt-4">
                      <p className="text-warning text-xs text-center">
                        ⚠️ After sending payment, please DM Crim
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Details for OnlyFans */}
              {selectedPaymentMethod === "onlyfans" && (
                <div className="bg-base-200 rounded-lg p-6 border-2 border-primary">
                  <div className="text-center mb-4">
                    <img
                      src="https://raw.githubusercontent.com/edent/SuperTinyIcons/master/images/svg/onlyfans.svg"
                      alt="OnlyFans"
                      className="w-16 h-16 mx-auto rounded-xl"
                    />
                    <h4 className="font-bold text-2xl text-base-content mt-2">
                      OnlyFans Payment
                    </h4>
                    <p className="text-primary text-3xl font-bold mt-2">
                      ${getInflatedPrice(selectedOption?.cost || 0)} USD
                    </p>
                    <p className="text-warning text-sm mt-1">
                      (+20% processing fee)
                    </p>
                  </div>
                  <div className="divider"></div>
                  <div className="space-y-3">
                    <p className="text-base-content/70 text-sm text-center">
                      Purchase through OnlyFans:
                    </p>
                    <a
                      href="https://onlyfans.com/crimsonkittenvip"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-lg w-full"
                    >
                      Visit OnlyFans Profile
                    </a>
                    <div className="bg-info/10 border border-info/20 rounded-lg p-3 mt-4">
                      <p className="text-info text-xs text-center">
                        💡 Please include "Crimcoins" in your tip note
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Details for Fansly */}
              {selectedPaymentMethod === "fansly" && (
                <div className="bg-base-200 rounded-lg p-6 border-2 border-primary">
                  <div className="text-center mb-4">
                    <img
                      src="https://cdn.beacons.ai/user_content/3EC1pHDtDwh0uQVFLS97BJVPVaH2/link_images/f0fc8769-832f-42ed-a13a-6d07a3165cb9.png"
                      alt="Fansly"
                      className="w-16 h-16 mx-auto rounded-xl"
                    />
                    <h4 className="font-bold text-2xl text-base-content mt-2">
                      Fansly Payment
                    </h4>
                    <p className="text-primary text-3xl font-bold mt-2">
                      ${getInflatedPrice(selectedOption?.cost || 0)} USD
                    </p>
                    <p className="text-warning text-sm mt-1">
                      (+20% processing fee)
                    </p>
                  </div>
                  <div className="divider"></div>
                  <div className="space-y-3">
                    <p className="text-base-content/70 text-sm text-center">
                      Purchase through Fansly:
                    </p>
                    <a
                      href="https://fansly.com/xcrimsonkitten"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-lg w-full"
                    >
                      Visit Fansly Profile
                    </a>
                    <div className="bg-info/10 border border-info/20 rounded-lg p-3 mt-4">
                      <p className="text-info text-xs text-center">
                        💡 Please include "Crimcoins" in your tip note
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="modal-action justify-center">
            <button className="btn btn-ghost" onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={closeModal}>close</button>
        </form>
      </dialog>

      <div ref={bottomRef} />
    </div>
  );
};

export default ShopDiscordTokens;
